/**
 * Chat Messages Panel — inbound/outbound bubbles
 * Uses exact Webflow classes: greeeter__chatwidget-bubble-*
 * Supports inline image reference badges, referenced image cards, and full-resolution lightbox
 */
import { el, formatTime } from '../utils.js';
import avatarImgUrl from './igreet-widget.webflow/images/smarties-head-p-130x130q80.png';

/**
 * Create the scrollable messages container
 */
export function createMessagesPanel() {
    return el('div', { className: 'greeeter__chatwidget-mainconvo' });
}

/**
 * Escape HTML utility
 */
function escapeHtml(str) {
    if (!str) return '';
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

let currentAudio = null;
let currentPlayingBtn = null;

// Preload voices
if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
    window.speechSynthesis.onvoiceschanged = () => {
        window.speechSynthesis.getVoices();
    };
    window.speechSynthesis.getVoices();
}

function getBestNaturalVoice() {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return null;
    const voices = window.speechSynthesis.getVoices() || [];
    if (!voices.length) return null;

    const enVoices = voices.filter(v => v.lang && v.lang.toLowerCase().startsWith('en'));
    const candidateList = enVoices.length ? enVoices : voices;

    const highQualityKeywords = [
        'natural', 'neural', 'premium', 'enhanced', 'siri',
        'ava', 'zoe', 'samantha', 'serena', 'oliver', 'kate', 'evan',
        'google us english', 'google uk english female', 'microsoft aria',
        'microsoft jenny', 'microsoft guy', 'microsoft christopher'
    ];

    let bestVoice = null;
    let highestScore = -999;

    for (const v of candidateList) {
        const nameLower = (v.name || '').toLowerCase();
        let score = 0;

        if (nameLower.includes('alex') || nameLower.includes('fred') || nameLower.includes('albert') || 
            nameLower.includes('bad news') || nameLower.includes('bells') || nameLower.includes('organ') ||
            nameLower.includes('cellos') || nameLower.includes('trinoids') || nameLower.includes('whisper')) {
            score -= 200;
        }

        for (const kw of highQualityKeywords) {
            if (nameLower.includes(kw)) score += 60;
        }

        if (nameLower.includes('natural') || nameLower.includes('neural')) score += 50;
        if (nameLower.includes('enhanced') || nameLower.includes('premium')) score += 35;
        if (nameLower.includes('online')) score += 20;
        if (v.lang === 'en-US' || v.lang === 'en_US' || v.lang === 'en-GB') score += 15;
        if (v.default) score += 5;

        if (score > highestScore) {
            highestScore = score;
            bestVoice = v;
        }
    }

    return bestVoice || candidateList[0] || null;
}

function fallbackWebSpeech(cleanText, buttonEl, resetBtn) {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
        resetBtn(buttonEl);
        return;
    }
    const utterance = new SpeechSynthesisUtterance(cleanText);
    utterance.rate = 0.98;
    utterance.pitch = 1.0;
    const naturalVoice = getBestNaturalVoice();
    if (naturalVoice) utterance.voice = naturalVoice;

    utterance.onend = () => {
        resetBtn(buttonEl);
        if (currentPlayingBtn === buttonEl) currentPlayingBtn = null;
    };
    utterance.onerror = () => {
        resetBtn(buttonEl);
        if (currentPlayingBtn === buttonEl) currentPlayingBtn = null;
    };
    window.speechSynthesis.speak(utterance);
}
export async function speakTts(text, buttonEl, voice = 'aura-asteria-en', serverUrl = '') {
    const resetBtn = (btn) => {
        if (!btn) return;
        btn.classList.remove('is-playing');
        btn.innerHTML = `<span style="font-size:10px; line-height:1; display:inline-block;">▶</span><span>Listen</span>`;
        btn.style.background = 'rgba(0, 78, 97, 0.08)';
        btn.style.color = '#004E61';
        btn.style.borderColor = 'rgba(0, 78, 97, 0.2)';
    };

    // If currently playing on this button, pause / toggle off
    if (currentAudio && !currentAudio.paused && currentPlayingBtn === buttonEl) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
        resetBtn(buttonEl);
        currentPlayingBtn = null;
        currentAudio = null;
        return;
    }

    if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
        if (currentPlayingBtn) resetBtn(currentPlayingBtn);
        currentPlayingBtn = null;
        currentAudio = null;
    }
    if (typeof window !== 'undefined' && 'speechSynthesis' in window && window.speechSynthesis.speaking) {
        window.speechSynthesis.cancel();
    }

    const cleanText = (text || '')
        .replace(/\[Image:\s*[^\]]+\]/gi, '')
        .replace(/!\[[^\]]*\]\([^)]+\)/gi, '')
        .replace(/<[^>]+>/g, '')
        .replace(/[*_#`~]/g, '')
        .trim();

    if (!cleanText) return;

    buttonEl.classList.add('is-playing');
    buttonEl.innerHTML = `<span style="font-size:10px; line-height:1; display:inline-block;">⏹</span><span>Stop</span>`;
    buttonEl.style.background = '#004E61';
    buttonEl.style.color = '#ffffff';
    buttonEl.style.borderColor = '#004E61';
    currentPlayingBtn = buttonEl;

    // 1. Try High-Definition Deepgram Aura Neural Audio via backend API
    try {
        const baseUrl = (serverUrl || '').replace(/\/+$/, '');
        const endpoint = `${baseUrl}/widget/tts?text=${encodeURIComponent(cleanText)}&voice=${encodeURIComponent(voice)}&ngrok-skip-browser-warning=true`;

        const res = await fetch(endpoint, {
            headers: {
                'ngrok-skip-browser-warning': 'true',
                'Accept': 'audio/mp3,audio/*;q=0.9,*/*;q=0.8'
            }
        });

        if (!res.ok) {
            throw new Error(`Deepgram TTS HTTP status ${res.status}`);
        }

        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        const audio = new Audio(blobUrl);
        currentAudio = audio;

        audio.onended = () => {
            URL.revokeObjectURL(blobUrl);
            resetBtn(buttonEl);
            if (currentPlayingBtn === buttonEl) {
                currentPlayingBtn = null;
                currentAudio = null;
            }
        };

        audio.onerror = (e) => {
            URL.revokeObjectURL(blobUrl);
            console.warn('[iGreet] Deepgram audio playback error, falling back to browser speech:', e);
            fallbackWebSpeech(cleanText, buttonEl, resetBtn);
        };

        await audio.play();
    } catch (e) {
        console.warn('[iGreet] Deepgram TTS request failed, falling back to browser speech:', e.message || e);
        fallbackWebSpeech(cleanText, buttonEl, resetBtn);
    }
}

/**
 * Add a message bubble to the panel
 * @param {HTMLElement} panel
 * @param {{ role: string, content: string, time: Date }} msg
 * @param {string} [logoUrl] - Optional custom logo URL to replace default avatar
 * @param {Object} [shell] - Shell reference for API calls and Lightbox
 */
export function addMessageToPanel(panel, msg, logoUrl, shell) {
    const isInbound = msg.role === 'assistant' || msg.role === 'bot';
    const timeStr = formatTime(msg.time);
    const rawContent = msg.content || '';

    if (isInbound) {
        // Inbound: avatar + bubble container
        const avatarDiv = el('div', { className: 'greeeter__chatwidget-chat-avatar' });
        avatarDiv.style.backgroundImage = `url('${logoUrl || avatarImgUrl}')`;
        avatarDiv.style.backgroundSize = 'cover';

        // Check for image references: [Image: filename.jpg] or ![alt](url)
        const imageMatches = [...rawContent.matchAll(/\[Image:\s*([^\]]+)\]/gi)];
        const mdImageMatches = [...rawContent.matchAll(/!\[([^\]]*)\]\(([^)]+)\)/gi)];

        let processedHtml = escapeHtml(rawContent);

        // Replace markdown images with badges
        if (mdImageMatches.length > 0) {
            mdImageMatches.forEach(match => {
                const fullToken = match[0];
                const alt = match[1] || 'Image';
                const escapedToken = escapeHtml(fullToken);
                const badgeHtml = `<span class="image-reference-badge">🖼️ ${escapeHtml(alt)}</span>`;
                processedHtml = processedHtml.replaceAll(escapedToken, badgeHtml);
            });
        }

        // Replace [Image: ...] tokens with temporary badges
        if (imageMatches.length > 0) {
            imageMatches.forEach(match => {
                const fullToken = match[0];
                const filename = (match[1] || '').trim();
                const escapedToken = escapeHtml(fullToken);
                const badgeHtml = `<span class="image-reference-badge" data-token="${escapeHtml(fullToken)}">🖼️ ${escapeHtml(filename)}</span>`;
                processedHtml = processedHtml.replaceAll(escapedToken, badgeHtml);
            });
        }

        const textDiv = el('div', { className: 'greeeter__chatwidget-bubble-text' });
        textDiv.innerHTML = processedHtml;

        const bubbleInbound = el('div', { className: 'greeeter__chatwidget-bubble-inbound' }, [textDiv]);
        
        // Text-to-Speech (TTS) Play Button
        const isTtsEnabled = (shell?.config?.features?.ttsEnabled !== false);
        let ttsBtn = null;
        if (isTtsEnabled) {
            const serverBase = shell?.widget?.api?.configUrl || shell?.widget?.options?.configUrl || shell?.config?.serverUrl || '';
            const voice = shell?.config?.theme?.voice || shell?.config?.voice || 'aura-asteria-en';
            ttsBtn = el('button', {
                className: 'chat-tts-btn',
                type: 'button',
                title: 'Play message aloud',
                style: 'display: inline-flex; align-items: center; justify-content: center; gap: 4px; background: rgba(0, 78, 97, 0.08); color: #004E61; border: 1px solid rgba(0, 78, 97, 0.2); border-radius: 9999px; padding: 2px 8px; font-size: 11px; font-weight: 600; font-family: inherit; cursor: pointer; transition: all 0.15s ease; outline: none; margin: 0 0 0 6px; line-height: 1.2; white-space: nowrap; box-sizing: border-box; height: 20px; min-height: 20px; max-height: 20px; width: auto; min-width: auto; -webkit-appearance: none; appearance: none; flex-shrink: 0;',
                onClick: (e) => {
                    e.stopPropagation();
                    speakTts(rawContent, ttsBtn, voice, serverBase);
                }
            });
            ttsBtn.innerHTML = `<span style="font-size:10px; line-height:1; display:inline-block;">▶</span><span>Listen</span>`;
        }

        const timeRow = el('div', {
            style: 'display: inline-flex; align-items: center; margin-top: 3px;'
        }, [
            el('div', { className: 'greeeter__chatwidget-chat-time' }, timeStr),
            ...(ttsBtn ? [ttsBtn] : [])
        ]);

        const bubbleContent = el('div', {
            style: 'display: flex; flex-direction: column; align-items: flex-start; max-width: 100%;',
        }, [
            bubbleInbound,
            timeRow,
        ]);

        const row = el('div', { className: 'greeeter__chatwidget-bubble-container' }, [
            avatarDiv, bubbleContent,
        ]);
        panel.appendChild(row);

        // If markdown images with direct URLs exist, render cards immediately
        if (mdImageMatches.length > 0) {
            let imgContainer = bubbleContent.querySelector('.chat-referenced-images');
            if (!imgContainer) {
                imgContainer = el('div', {
                    className: 'chat-referenced-images',
                    style: 'margin-top: 8px; margin-bottom: 2px; display: flex; flex-direction: column; gap: 6px; width: 100%; max-width: 240px;'
                });
                bubbleContent.insertBefore(imgContainer, timeRow);
            }
            mdImageMatches.forEach(match => {
                const alt = match[1] || 'Image';
                const url = match[2];
                const card = el('div', {
                    className: 'chat-referenced-image-card',
                    style: 'border-radius: 10px; overflow: hidden; border: 1px solid #e2e8f0; background: #ffffff; padding: 6px; box-sizing: border-box; width: 100%; max-width: 240px; box-shadow: 0 2px 6px rgba(0,0,0,0.06); cursor: pointer; transition: transform 0.15s ease;'
                }, [
                    el('img', {
                        src: url,
                        alt: alt,
                        className: 'chat-referenced-image-img',
                        loading: 'lazy',
                        style: 'width: 100%; height: auto; max-height: 200px; object-fit: contain; border-radius: 6px; display: block; background: #f8fafc;',
                        onClick: () => shell?.openLightbox?.({ url, title: alt }),
                    }),
                    alt ? el('div', { className: 'chat-referenced-image-title', style: 'font-size: 12px; font-weight: 600; color: #0f172a; margin-top: 6px; padding: 0 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;' }, alt) : null,
                ].filter(Boolean));
                imgContainer.appendChild(card);
            });
        }

        // Asynchronously resolve [Image: filename] tags via backend
        if (imageMatches.length > 0 && shell?.widget?.api?.resolveImage) {
            Promise.all(imageMatches.map(async (match) => {
                const fullToken = match[0];
                const filename = (match[1] || '').trim();
                try {
                    const data = await shell.widget.api.resolveImage(filename);
                    return {
                        token: fullToken,
                        id: data.id,
                        title: data.title || filename,
                        description: data.description || '',
                        url: data.url,
                    };
                } catch (err) {
                    console.warn('[iGreet] Could not resolve image:', filename, err);
                    return null;
                }
            })).then((resolvedList) => {
                const validList = resolvedList.filter(Boolean);
                if (validList.length === 0) return;

                // Update badges with resolved titles
                validList.forEach(img => {
                    const badge = textDiv.querySelector(`[data-token="${img.token}"]`);
                    if (badge) {
                        badge.textContent = `🖼️ ${img.title}`;
                    }
                });

                // Append image cards below text bubble, before timestamp
                let imgContainer = bubbleContent.querySelector('.chat-referenced-images');
                if (!imgContainer) {
                    imgContainer = el('div', {
                        className: 'chat-referenced-images',
                        style: 'margin-top: 8px; margin-bottom: 2px; display: flex; flex-direction: column; gap: 6px; width: 100%; max-width: 240px;'
                    });
                    bubbleContent.insertBefore(imgContainer, timeRow);
                }

                validList.forEach(img => {
                    const showDesc = img.description && img.description.trim() !== (img.title || '').trim();
                    const card = el('div', {
                        className: 'chat-referenced-image-card',
                        style: 'border-radius: 10px; overflow: hidden; border: 1px solid #e2e8f0; background: #ffffff; padding: 6px; box-sizing: border-box; width: 100%; max-width: 240px; box-shadow: 0 2px 6px rgba(0,0,0,0.06); cursor: pointer; transition: transform 0.15s ease;'
                    }, [
                        el('img', {
                            src: img.url,
                            alt: img.title,
                            className: 'chat-referenced-image-img',
                            loading: 'lazy',
                            style: 'width: 100%; height: auto; max-height: 200px; object-fit: contain; border-radius: 6px; display: block; background: #f8fafc;',
                            onClick: () => shell?.openLightbox?.({
                                url: img.url,
                                title: img.title,
                                description: img.description,
                            }),
                        }),
                        img.title ? el('div', { className: 'chat-referenced-image-title', style: 'font-size: 12px; font-weight: 600; color: #0f172a; margin-top: 6px; padding: 0 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;' }, img.title) : null,
                        showDesc ? el('div', { className: 'chat-referenced-image-desc', style: 'font-size: 11px; color: #64748b; padding: 0 2px; margin-top: 2px;' }, img.description) : null,
                    ].filter(Boolean));
                    imgContainer.appendChild(card);
                });

                panel.scrollTop = panel.scrollHeight;
            });
        }
    } else {
        // Outbound: right-aligned row
        const row = el('div', { className: 'greeeter__chatwidget-bubble-outbound-row' }, [
            el('div', { className: 'greeeter__chatwidget-bubble-outbound' }, [
                el('div', {}, rawContent),
            ]),
            el('div', { className: 'greeeter__chatwidget-chat-time' }, timeStr),
        ]);
        panel.appendChild(row);
    }

    panel.scrollTop = panel.scrollHeight;
}
